'use client'
import type { FC, ReactElement, ReactNode } from 'react'
import { useMemo } from 'react'
import type { FlexProps } from '@chakra-ui/react'
import { Button, Flex, HStack } from '@chakra-ui/react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { ContentContainer, GrandIconButton, GrandNextLink, HoverableComponent } from 'ui'
import { MenuItem, MenuItemComponent } from './MenuItem'

export interface TopNavContentProps extends FlexProps {
  menuItems: { other: MenuItem[] }
  logo: ReactNode
  onMenuHover: (isInside: boolean) => void
  onMenuToggle: (menuItems: MenuItem[]) => void
  toggleIcon: ReactElement
  isHidden?: boolean
}

const TopNavContent: FC<TopNavContentProps> = ({
  onMenuHover,
  onMenuToggle,
  toggleIcon,
  menuItems,
  logo,
  isHidden: _isHidden = true,
  ...props
}) => {
  const pathname = usePathname()

  const menuItemsFull = useMemo(() => {
    const mi = [...menuItems.other]
    // removes non-serializable values so we can pass then through extraArgs
    return mi.map(({ onClick, onHoverLeave, onHoverEnter, ...rest }) => ({
      ...rest
    }))
  }, [menuItems])

  const handleMenuToggle = () => {
    onMenuToggle(menuItemsFull)
  }

  return (
    <Flex w="100%" {...props}>
      <ContentContainer justify="center" overflow="hidden" py={0} spacing={0} w="full">
        {/* Mobile layout */}
        <HoverableComponent
          alignItems="center"
          onHover={onMenuHover}
          display={{
            base: 'flex',
            md: 'none'
          }}
        >
          <HStack align="center" flex={1} p={1} w="full" justify="space-between">
            <GrandNextLink aria-label="home" cursor="pointer" href="/">
              {logo}
            </GrandNextLink>

            <GrandIconButton
              aria-label="open-menu"
              bg="none"
              color="text.default"
              icon={toggleIcon}
              onClick={handleMenuToggle}
              shadow="none"
              size="sm"
            />
          </HStack>
        </HoverableComponent>

        {/* Desktop layout */}
        <HStack
          display={{
            base: 'none',
            md: 'flex'
          }}
          fontWeight="medium"
          justify="space-between"
          py={0}
          textAlign="center"
          w="full"
        >
          {/* Logo on left */}
          <GrandNextLink aria-label="home" cursor="pointer" href="/">
            {logo}
          </GrandNextLink>

          {/* Menu items and CTA on right */}
          <HStack gap={10}>
            {menuItems.other.map((item) => (
              <MenuItemComponent
                key={item.id}
                {...item}
                isCurrent={pathname === item.path}
                text={item.text}
              />
            ))}

            <Link href="/signup">
              <Button
                bg="background.dark"
                color="white"
                fontWeight={500}
                px={6}
                py={2}
                borderRadius="md"
                _hover={{ bg: 'gray.700' }}
                size="sm"
              >
                Let&apos;s Talk
              </Button>
            </Link>
          </HStack>
        </HStack>
      </ContentContainer>
    </Flex>
  )
}

export default TopNavContent
